import { float, max, mix, mx_noise_float, normalize, positionWorld, sin, smoothstep, vec3 } from 'three/tsl';
import type { AtmosphereDef } from '../../engine/atmosphere/AtmosphereDef';
import type { LightRig } from '../../engine/lighting/LightRig';
import type { TerrainProfile } from '../../engine/terrain/TerrainProfile';
import {
  type BakedNoise,
  broadNoise,
  noiseWhen,
  reliefShade,
  ridgeOf,
  type TerrainStyle,
} from '../../engine/terrain/TerrainStyle';
import type { GrassStyle, TreeStyle } from '../../engine/vegetation/VegetationStyle';
import type { HollowSceneDef } from '../scenes/HollowScene';
import { boulders } from './rocks';

type N = any;

/**
 * Emberdeep: a 16 km hollow with no sun, lit from below by a lava sea and the
 * glowing rivers that run down its black mountains. The lava flares region by region,
 * at random: a surge that lights the smoke and the slopes around it, then a slow dying
 * back to a dull crust, so no two places keep time (LightRig's chaotic glow). Smoke
 * hangs low and glows orange over the flows.
 */
export const EMBERDEEP_TERRAIN: TerrainProfile = {
  continents: { wavelength: 7000, octaves: 4, lacunarity: 2.05, gain: 0.5 },
  continentHeight: 140,
  landBias: 0.2,
  mountainMask: { wavelength: 5000, octaves: 2, lacunarity: 2.1, gain: 0.5 },
  ridges: { wavelength: 2400, octaves: 8, lacunarity: 2.02, gain: 0.52 },
  ridgeHeight: 1100,
  ridgeDamping: 1.2,
  hills: { wavelength: 500, octaves: 4, lacunarity: 2.05, gain: 0.5 },
  hillHeight: 40,
  detail: { wavelength: 50, octaves: 6, lacunarity: 2.07, gain: 0.5 },
  detailHeight: 3.5,
  // Ash slopes gully deeply.
  erosion: { wavelength: 400, octaves: 5, lacunarity: 1.97, gain: 0.55, strength: 0.45 },
  snowLine: 1e9,
};

/** Low smoke that absorbs a lot and glows faintly with the fire beneath it. */
export const EMBERDEEP_AIR: AtmosphereDef = {
  rayleigh: [1.0e-6, 1.2e-6, 1.6e-6],
  rayleighHeight: 3000,
  mie: 2.4e-5,
  mieHeight: 700,
  mieAbsorption: 0.8,
  mieG: 0.55,
  ambientScatter: 0.5,
  airGlow: [0.3e-5, 0.09e-5, 0.015e-5],
};

/** The lava's flare (its quiet level … 1, more in an eruption) at a hollow-local position. */
const phaseAt = (rig: LightRig, local: N): N => rig.glowAt(normalize(local));
/** The flare where the fragment being shaded is (any material). */
const phaseHere = (rig: LightRig): N => phaseAt(rig, positionWorld.sub(rig.centerRel));

const HOT = vec3(1.9, 0.52, 0.07);
const WARM = vec3(0.8, 0.18, 0.025);

/** The palette's broad noise terms, carried by the tiles (TerrainStyle.bakedNoise). */
const EMBERDEEP_BAKED: BakedNoise[] = [
  { wavelength: 1600, offset: [9.9, 2.4, 6.1] },
  { wavelength: 900 },
  { wavelength: 2600, offset: [4.2, 7.1, 0.6] },
  { wavelength: 700, offset: [8.1, 2.2, 5.3] },
];

const emberdeepStyle = (rig: LightRig): TerrainStyle => ({
  bakedNoise: EMBERDEEP_BAKED,
  forestMask(ctx) {
    const { p, h, slope } = ctx;
    // Charred woods keep to the gullies.
    const n = broadNoise(ctx, p, 1600, [9.9, 2.4, 6.1]).sub(ridgeOf(ctx).all.mul(0.2));
    return smoothstep(0.15, 0.4, n)
      .mul(float(1).sub(smoothstep(0.2, 0.38, slope)))
      .mul(float(1).sub(smoothstep(250, 420, h)));
  },

  land(ctx) {
    const { p, h, slope, near, mid } = ctx;
    const phase = phaseAt(rig, p);
    const macro = broadNoise(ctx, p, 900);
    const fine = mx_noise_float(p.mul(1 / 70));
    const patch = mx_noise_float(p.mul(1 / 10));
    // Close by only, in a branch (multiplying by 0 saves nothing).
    const grain = noiseWhen(p.mul(1 / 1.5), near).mul(near);

    // Black basalt, grey ash on the flats, rusty strata on the slopes, sulphur by the lava.
    const basalt = mix(vec3(0.05, 0.046, 0.045), vec3(0.11, 0.095, 0.088), macro.mul(0.5).add(0.5));
    const ash = vec3(0.2, 0.19, 0.18).mul(patch.mul(0.12).add(1));
    let land: N = mix(basalt, ash, smoothstep(-0.2, 0.7, macro.add(fine.mul(0.25))).mul(0.7));
    // The landform: ash drifts into the gullies, ribs of bare basalt stand along the crests.
    const ridge = ridgeOf(ctx);
    const gully = smoothstep(0.1, 0.65, ridge.all.negate());
    land = mix(land, ash.mul(0.85), gully.mul(0.35));
    land = land.mul(grain.mul(0.3).add(1));
    // Layers of old flows and tuff: two waves, coming and going along the face, mostly dark
    // basalt with rust layers here and there (one even sine drew zebra stripes on every face).
    const layerAt = h.mul(0.12).add(fine.mul(5)).add(patch.mul(1.2));
    const run = sin(h.mul(0.12 * 0.29).add(fine.mul(1.5)))
      .mul(0.45)
      .add(0.55);
    const layers = sin(layerAt)
      .mul(0.65)
      .add(sin(layerAt.mul(2.37).add(1.3)).mul(0.35))
      .mul(run);
    const band = smoothstep(-0.15, 0.85, layers).mul(0.8);
    const strata = mix(vec3(0.05, 0.035, 0.03), vec3(0.19, 0.09, 0.05), band).mul(grain.mul(0.25).add(1));
    const rocky = smoothstep(0.2, 0.36, slope.add(fine.mul(0.05)).add(ridge.all.mul(0.09)));
    land = mix(land, strata, rocky);
    // Narrow gullies read as dark creases.
    land = land
      .mul(float(1).sub(smoothstep(0.15, 0.8, ridge.narrow.negate()).mul(0.3)))
      .mul(reliefShade(ridge.relief));
    const charred = vec3(0.045, 0.04, 0.038);
    land = mix(land, charred, this.forestMask(ctx).mul(0.7));
    const sulphur = vec3(0.55, 0.47, 0.1);
    const nearLava = float(1).sub(smoothstep(0.5, 6, h));
    // The 10 m patterns (sulphur, hot cracks) fade to their mean beyond a few hundred metres:
    // further away they sparkled from pixel to pixel.
    const fine10 = float(1).sub(smoothstep(150, 700, ctx.dist));
    const sulphurM = mix(float(0.06), smoothstep(0.35, 0.6, patch.add(fine.mul(0.4))), fine10);
    land = mix(land, sulphur, nearLava.mul(sulphurM).mul(0.8));

    // Lava rivers along the valley lines of a ridged pattern, hot cracks by the sea,
    // glowing seams in the mountain faces.
    // Stretched along one axis so channels run like flows rather than loops.
    const chan = float(1).sub(
      mx_noise_float(p.mul(vec3(1 / 300, 1 / 1400, 1 / 300)).add(vec3(3.3, 8.8, 1.1))).abs(),
    );
    const volcanic = smoothstep(0.0, 0.3, broadNoise(ctx, p, 2600, [4.2, 7.1, 0.6]));
    // Lava also runs down the deepest gullies of the volcanic slopes, as real flows follow them.
    const gullyLava = smoothstep(0.62, 0.8, ridge.all.negate())
      .mul(volcanic)
      .mul(smoothstep(0.06, 0.16, slope))
      .mul(float(1).sub(smoothstep(250, 420, h)));
    // The winding channels flow only where the ground gathers into gullies.
    const rivers = max(
      smoothstep(0.982, 0.994, chan.add(fine.mul(0.006)))
        .mul(volcanic)
        .mul(smoothstep(0.1, 0.5, gully))
        .mul(float(1).sub(smoothstep(180, 320, h)))
        .mul(float(1).sub(smoothstep(0.3, 0.5, slope))),
      gullyLava,
    );
    const cracks = nearLava.mul(mix(float(0.82), smoothstep(0.55, 0.7, float(1).sub(patch.abs())), fine10));
    const seamsHere = rocky.mul(mid);
    const seams = smoothstep(0.965, 0.99, float(1).sub(noiseWhen(p.mul(1 / 16), seamsHere).abs()))
      .mul(smoothstep(0.1, 0.4, broadNoise(ctx, p, 700, [8.1, 2.2, 5.3])))
      .mul(seamsHere);
    // Light thrown by the lava onto the rock around it: the shores of the lava sea and
    // the banks of each river glow faintly (a cheap stand-in for local light).
    const shoreLight = float(1).sub(smoothstep(0, 14, h));
    const bankLight = smoothstep(0.93, 0.982, chan).mul(volcanic).mul(float(1).sub(rivers));
    const emissive = HOT.mul(rivers)
      .add(WARM.mul(cracks.mul(0.8).add(seams.mul(0.5))))
      .add(WARM.mul(shoreLight.mul(0.1).add(bankLight.mul(0.18))))
      .mul(phase);
    land = mix(land, vec3(0.05, 0.02, 0.01), rivers);
    const roughness = mix(float(0.9), float(0.75), rocky);
    return { albedo: land, roughness, emissive };
  },

  // The "sea" is lava: a dark crust drifting over molten rock.
  water: {
    shallow: [0.08, 0.02, 0.005],
    deep: [0.06, 0.015, 0.004],
    depthScale: 10,
    foam: [0.05, 0.035, 0.03],
    roughness: 0.75,
    emissive: ({ p, h, time, near, mid }) => {
      const flow = vec3(time.mul(0.05), time.mul(0.02), time.mul(-0.03));
      const crust = smoothstep(
        -0.05,
        0.45,
        mx_noise_float(p.mul(1 / 9).add(flow))
          .mul(near)
          .add(mx_noise_float(p.mul(1 / 40).add(flow.mul(0.5))).mul(mid.mul(0.6)))
          .add(float(1).sub(mid).mul(0.25)),
      );
      const deep = smoothstep(0, 6, h.negate()).mul(0.4).add(0.6);
      return mix(vec3(2.4, 0.7, 0.1), vec3(0.3, 0.045, 0.008), crust)
        .mul(deep)
        .mul(phaseAt(rig, p));
    },
  },
});

const emberdeepGrass: GrassStyle = {
  lush: [0.07, 0.06, 0.05],
  meadow: [0.13, 0.11, 0.09],
  tip: [0.22, 0.17, 0.12],
  roughness: 0.8,
  minHeight: 8,
  height: 0.55,
};

const emberdeepTrees = (rig: LightRig): TreeStyle => ({
  a: {
    shape: 'conifer',
    bark: [0.03, 0.025, 0.02],
    crown: [
      [0.035, 0.03, 0.027],
      [0.065, 0.05, 0.04],
    ],
    bendUp: 0.3,
  },
  b: {
    shape: 'broadleaf',
    bark: [0.05, 0.03, 0.02],
    crown: [
      [0.1, 0.03, 0.01],
      [0.18, 0.05, 0.015],
    ],
    bendUp: 0.4,
    // Ember trees: their crowns smoulder, and flare with the lava around them.
    glow: () => vec3(1.0, 0.32, 0.06).mul(phaseHere(rig).mul(0.8).add(0.2)),
    crownGlow: 0.16,
    // A red glow on the ground beneath each smouldering crown.
    light: { height: 4.5, radius: 10, gain: 5 },
  },
  aShare: [0.65, 0.9],
  loneTrees: 0.012,
  minHeight: 8,
});

export const EMBERDEEP: HollowSceneDef = {
  id: 'emberdeep',
  name: 'Emberdeep',
  center: [-9.1e6, -3.4e6, -6.2e6],
  radius: 8_000,
  seed: 41,
  sun: null,
  terrain: EMBERDEEP_TERRAIN,
  air: EMBERDEEP_AIR,
  clouds: null,
  veil: {
    litFraction: 0.5,
    softness: 0.4,
    dayLength: 300,
    spinAxis: [0.2, 0.9, 0.4],
    duskColor: [1.0, 0.4, 0.1],
    nightAmbient: [0.55, 0.45, 0.42],
    rimAmbient: [1.3, 0.8, 0.5],
    rimAmbientStrength: 0.3,
  },
  // Quiet lava glows at a tenth of a flare (it was over half: its "night" was hardly dark).
  glow: { color: [0.78, 0.62, 0.58], day: 0.5, night: 0.05, pulse: 'chaotic' },
  worldAlbedo: [0.06, 0.05, 0.045],
  style: emberdeepStyle,
  grass: () => emberdeepGrass,
  trees: emberdeepTrees,
  rocks: (rig) =>
    boulders(
      [
        [0.06, 0.05, 0.05],
        [0.12, 0.1, 0.09],
      ],
      [
        [0.08, 0.04, 0.03],
        [0.12, 0.05, 0.03],
      ],
      {
        // Some stones are still hot, and flare with the lava around them.
        glow: () => vec3(0.9, 0.22, 0.04).mul(0.12).mul(phaseHere(rig).mul(0.8).add(0.2)),
        glowShare: 0.15,
        minHeight: 4,
      },
    ),
  motes: (rig) => ({
    count: 1800,
    radius: 45,
    size: [0.04, 0.12],
    colors: [
      [1.0, 0.45, 0.08],
      [1.0, 0.75, 0.3],
    ],
    brightness: 3.5,
    drift: 0.35,
    rise: 1.2,
    glow: () => phaseAt(rig, rig.centerRel.negate()).mul(0.75).add(0.25),
  }),
  spawn: { dir: [0.5, 0.3, -0.8], minHeight: 12, maxHeight: 120 },
  exposureKey: 3.6,
  hints: [
    'Lit from below by a sea of lava.',
    'Each lake and river of lava flares on its own, then dies back. V to fly; hold T to hurry it.',
  ],
  waterSkyTint: [0.25, 0.18, 0.15],
  ambience: {
    rumble: { gain: 0.35 },
    wind: { gain: 0.1, cutoff: 300 },
    events: [{ kind: 'crackle', rate: 1.2, gain: 0.1 }],
    echo: 0.15,
  },
};
